import { Injectable, Injector } from '@angular/core';
import { ApiService } from '../../../core/api.service';
import { Base64Service } from '../../../core/base64.service';
import { BrowserSupportService } from '../../../core/browser-support.service';
import { SyncCryptService } from '../../../core/crypt/sync-crypt.service';
import { LoggerService } from '../../../core/logger.service';
import { NotificationsService } from '../../../core/notifications.service';
import { SyncCookieService } from '../../../core/sync-cookie.service';
import { UrlService } from '../../../core/url.service';
import { UserService } from '../../../core/user.service';
import { ValidateService } from '../../../core/validate.service';
import { FileListService } from '../../../file-list/services/file-list.service';
import { LinkFileListService } from '../../../link-consume/services/link-file-list.service';
import { compare } from '../../../shared/func';
import { BaseApiInput, BlendEvent } from '../../../shared/models';
import { BroadcastService } from '../../../shared/services/broadcast.service';
import { DownloadService } from '../download/download.service';
import { TransferItemService } from './transfer-item.service';
import {
  IPathData,
  ITransferItem,
  ITransferItemDownload,
  ITransferItemUpload,
  ITransferLatestItem,
  ITransferNeedKeys,
  ITransferQueueState,
  ITransferView,
  StreamItem,
} from 'src/sync';
import {
  TransferQueueType,
  TRANSFER_STATUS,
  VALID_DOWNLOAD_STATUS,
  VALID_UPLOAD_STATUS,
} from '../../models/transfer.model';
import { FileUploader } from '../../file-uploader';
import { FileItem } from 'src/app/shared/models/files.model';
import { FileItemService } from 'src/app/file/file-item.service';
import { BlendService } from 'src/app/shared/services/blend.service';
import { ExternalNavigationHandlerService } from './external-navigation-handler.service';
import { TransferType } from '../../models/transfer-type';
import { MAX_ACTIVE_DOWNLOADS, MAX_ACTIVE_UPLOADS, MAX_ACTIVE_UPLOADS_MOBILE } from '../../transfer.constants';
import { BrowserService } from 'src/app/file/browser.service';
import { FeatureFlags } from 'src/app/shared/models';

@Injectable({
  providedIn: 'root',
})
export class BuildTransferItemService {
  public view: ITransferView;
  public ACT_DOWNLOAD = 1;
  public ACT_PREVIEW = 2;
  public ACT_STREAM_PDF = 3;
  public ACT_STREAM_DOC = 4;

  private cwd: FileItem;
  private linkCwd: FileItem;

  // Maximum concurrent uploads (depends on web/ mobile browser)
  private maxActiveUploads: number;

  constructor(
    private notificationsService: NotificationsService,
    private downloadService: DownloadService,
    private fileListService: FileListService,
    private browserSupportService: BrowserSupportService,
    private syncCookieService: SyncCookieService,
    private transferItemService: TransferItemService,
    private validateService: ValidateService,
    private base64Service: Base64Service,
    private loggerService: LoggerService,
    private syncCryptService: SyncCryptService,
    private urlService: UrlService,
    private userService: UserService,
    private apiService: ApiService,
    private linkFileListService: LinkFileListService,
    private broadcastService: BroadcastService,
    private injector: Injector,
    private fileItemService: FileItemService,
    private blendService: BlendService,
    private externalNavigationHandlerService: ExternalNavigationHandlerService,
    private browserService: BrowserService,
  ) {
    this.view = {
      download: {
        queue: [],
        queueIdx: 0,
        isProcessing: false,
        activeItemCount: 0,
      },
      upload: {
        queue: [],
        queueIdx: 0,
        isProcessing: false,
        activeItemCount: 0,
      },
      single: undefined,
      isUpload: false,
      isDownload: false,
      enabled: this.browserSupportService.testXhr2(),
      pending: 0,
      cannotViewSave: false,
      transferLabel: 'Upload',
      isMinimized: false,
    };
    this.fileListService.getCwdSubscription().subscribe((data) => {
      if (data) {
        this.cwd = data;
      }
    });

    this.linkFileListService.getCwdSubscription().subscribe((data) => {
      if (data) {
        this.linkCwd = data;
      }
    });
    this.externalNavigationHandlerService.addHandler(this.navigationHandler);

    this.maxActiveUploads = this.getMaxActiveUploads();
  }

  public getMaxActiveUploads(): number {
    if (this.browserService.isMobileBrowser()) {
      return MAX_ACTIVE_UPLOADS_MOBILE;
    }

    const payload = this.userService.getFeatureFlagPayload(FeatureFlags.CONCURRENT_UPLOAD);
    if (payload && typeof payload.MAX_ACTIVE_UPLOADS === 'number' && payload.MAX_ACTIVE_UPLOADS > 0) {
      return payload.MAX_ACTIVE_UPLOADS;
    }

    return MAX_ACTIVE_UPLOADS;
  }

  public async addToQueue(results: ITransferItem[], queueType: string): Promise<any> {
    const view = this.view[queueType];
    if (results.length) {
      view.queue = view.queue.concat(results);
      view.queue.sort((a, b) => compare(a._initTime, b._initTime, true));
    }
    setTimeout(() => this.runQueue(queueType), 0);
    return true;
  }

  private shouldProcessQueue(queueType: string): boolean {
    const view: ITransferQueueState = this.view[queueType];
    const maxActiveItems = queueType === TransferQueueType.UPLOAD ? this.maxActiveUploads : MAX_ACTIVE_DOWNLOADS;
    return view.activeItemCount < maxActiveItems;
  }

  private shouldStopQueue(queueType: string): boolean {
    const view: ITransferQueueState = this.view[queueType];
    return view.activeItemCount === 0;
  }

  public async runQueue(queueType: string): Promise<void> {
    const view: ITransferQueueState = this.view[queueType];
    if (view.queue.length) {
      const next = this.getLatestItem(view.queue);
      this.view.pending = next.pending;
      if (next.idx > -1 && this.shouldProcessQueue(queueType)) {
        this.notificationsService.stopNotificationLoop();
        view.isProcessing = true;
        view.queueIdx = next.idx;
        const tItem = view.queue[next.idx];
        if (tItem) {
          this.view.transferLabel = 'Transferring';
          if (tItem.type == TransferType.UPLOAD) {
            this.view.isUpload = true;
            let fileUploader = new FileUploader(this.injector);

            fileUploader
              .uploadItem(<ITransferItemUpload>tItem)
              .then((result) => {
                fileUploader = null;
                tItem.sync_id = result.syncId;
                this.runQueue(queueType);
              })
              .catch((error) => {
                this.handleError(error, tItem);
                fileUploader = null;
                this.runQueue(queueType);
              });
          } else {
            try {
              this.view.isDownload = true;
              await this.downloadService.downloadItem(<ITransferItemDownload>tItem);
            } catch (error) {
              this.handleError(error, tItem);
            }
          }
          this.runQueue(queueType);
        }
      } else if (this.shouldStopQueue(queueType)) {
        this.notificationsService.startNotificationLoop();
        view.isProcessing = false;
        if (queueType === TransferQueueType.UPLOAD) {
          this.view.isUpload = false;
        } else if (queueType === TransferQueueType.DOWNLOAD) {
          this.view.isDownload = false;
        }
        if (!this.view.download.isProcessing && !this.view.upload.isProcessing) {
          this.view.transferLabel = 'Upload';
        }
      }
    }
  }

  private handleError(data: any, tItem: ITransferItem): void {
    if (typeof data === 'object' && data.errors && data.errors.length) {
      tItem.status = data.errors[0].error_code || TRANSFER_STATUS.STATUS_ERROR;
      this.loggerService.error(data.errors[0].error_msg);
    } else if (data.errcode) {
      tItem.status = data.errcode;
    } else if (data.code) {
      tItem.status = data.code;
    } else {
      tItem.status = TRANSFER_STATUS.STATUS_ERROR;
    }
    this.loggerService.error(
      [
        'Transfer encountered an error',
        ' type: ',
        tItem.type,
        ' status: ',
        tItem.status,
        ' size: ',
        tItem.filesize,
      ].join(''),
    );
  }

  public getLatestItem(items: ITransferItem[]): ITransferLatestItem {
    let idx = -1,
      pendingBytes = 0,
      pending = 0,
      activeUploadsCount = 0,
      activeDownloadsCount = 0;
    for (let i = 0, len = items.length; i < len; i++) {
      if (items[i].status == TRANSFER_STATUS.STATUS_WAITING) {
        if (idx === -1) {
          idx = i;
        }
        pending++;
        pendingBytes += items[i].filesize;
      }
      if (items[i].type === TransferType.UPLOAD && VALID_UPLOAD_STATUS.includes(items[i].status)) {
        activeUploadsCount++;
      }
      if (items[i].type === TransferType.DOWNLOAD && VALID_DOWNLOAD_STATUS.includes(items[i].status)) {
        activeDownloadsCount++;
      }
    }
    this.view.upload.activeItemCount = activeUploadsCount;
    this.view.download.activeItemCount = activeDownloadsCount;
    return {
      pending: pending,
      pendingBytes: pendingBytes,
      idx: idx,
    };
  }

  public async getCompatUrl(file: FileItem, action: number): Promise<string> {
    const keyData = await this.getFileKeys([file]);
    const tItem = await this.mkDownloadItem(file, keyData);
    const url = await this.mkCompatUrl(tItem, action);
    if (action == this.ACT_DOWNLOAD) {
      this.blendService.track(BlendEvent.DOWNLOAD_FILE, {
        isCompat: 'Yes',
        fileSize: file.filesize,
        mimeType: file.mime_type,
      });
    }
    return url;
  }

  /**
   * Gets the data and share keys for an array of files
   * @param files
   */
  public getFileKeys(files: FileItem[]): Promise<IPathData> {
    const needKeys: ITransferNeedKeys[] = [];
    let isLink = false;
    for (let i = 0, len = files.length; i < len; i++) {
      if (files[i].share_id && files[i].sync_id && files[i].blob_id) {
        const ext = files[i].name.substr(files[i].name.lastIndexOf('.') + 1).toLowerCase();
        needKeys.push({
          share_id: files[i].share_id,
          blob_id: files[i].blob_id,
          sync_id: files[i].sync_id,
          ext: this.base64Service.encode(ext),
          link_cachekey: files[i].linkID,
          size: files[i].size,
          user_id: this.userService.get('id'),
        });
      }
      if (files[i].linkID && isLink === false) {
        isLink = true;
      }
    }
    const pubkey = !isLink ? this.userService.get('pubkey_id') : undefined;
    return this.apiService.execute<any>('pathdata', {
      pathitems: needKeys,
      pubkey: pubkey,
    });
  }

  public async mkDownloadItem(file: FileItem, keyData: any): Promise<ITransferItemDownload> {
    let curDataKey, encDataKey, encShareKey, previewtoken;

    // TODO: This is a hack when we don't have the keydata in the Webpanel
    // TODO: Please sign off on this Richard.
    if (keyData) {
      curDataKey = keyData.datakeys[file.sync_id];
      encDataKey = curDataKey.enc_data_key;
      encShareKey = keyData.sharekeys[curDataKey.share_key_id];
      previewtoken = keyData.previewtoken || undefined;
    } else {
      curDataKey = {
        share_key_id: undefined,
        share_id: undefined,
        share_sequence: undefined,
      };
    }
    const result = await this.transferItemService
      .get({
        context: file.context,
        type: TransferType.DOWNLOAD,
        blob_id: file.blob_id,
        blobtype: file.blobtype || 'btFILE',
        filename: file.name,
        name: file.name,
        filesize: file.size,
        filetype: file.filetype,
        mimetype: file.filetype?.mimetype,
        filedate: file.date,
        cachekey: file.cachekey,
        link_is_pro: file.link_is_pro || 0,
        link_owner_id: file.link_owner_id || 0,
        linkID: file.linkID || undefined,
        linkpasswordlock: file.linkpasswordlock || undefined,
        storage_backend: file.storage_backend,
        previewtoken: previewtoken,
        sync_id: file.sync_id,
        enc_data_key: encDataKey,
        enc_share_key: encShareKey,
        share_key: file.share_key || undefined,
        share_key_id: curDataKey.share_key_id,
        share_id: curDataKey.share_id,
        share_sequence: curDataKey.share_sequence,
        enc_name: file.enc_name,
        status: TRANSFER_STATUS.STATUS_WAITING,
      })
      .catch((err) => {
        this.loggerService.error('An error occurred creating download item');
        throw err;
      });
    return <ITransferItemDownload>result;
  }

  public async mkuploadItem(fileObj: File, customCWD?: FileItem): Promise<ITransferItemUpload> {
    let itemStatus = 0;
    if (!fileObj) {
      this.loggerService.warn('An empty file object was passed to mkuploadItem');
      itemStatus = TRANSFER_STATUS.STATUS_ERR_NAME;
    }
    const customCWDId = customCWD ? customCWD.sync_id : 0;
    const cwdId = this.cwd ? this.cwd.sync_id : 0;
    // safari does not use 'lastModifiedDate' so if undefined, set the
    // date to now.
    const filedate = fileObj.lastModified || Date.now();

    const tItem = await this.transferItemService
      .get({
        type: TransferType.UPLOAD,
        sync_pid: customCWDId || cwdId,
        filename: fileObj.name.trim() || '',
        filesize: fileObj.size || 0,
        fileobj: fileObj,
        status: itemStatus,
        filedate: new Date(filedate).getTime(),
      })
      .catch((err) => {
        throw err;
      });
    if (!this.validateService.isNameValid(tItem.filename)) {
      this.loggerService.warn('File name ' + tItem.filename + ' is not allowed to be uploaded');
      tItem.status = TRANSFER_STATUS.STATUS_ERR_NAME;
    }
    return <ITransferItemUpload>tItem;
  }

  public async mkPublicUploadItem(fileObj: File): Promise<ITransferItemUpload> {
    const tItem = await this.mkuploadItem(fileObj).catch((err) => {
      throw err;
    });
    const cwd = this.linkCwd;
    tItem.filename = this.linkFileListService.getValidFileName(tItem.filename);
    tItem.share_id = cwd.share_id;
    tItem.share_key = cwd.share_key;
    tItem.share_sequence = cwd.share_sequence;
    tItem.share_key_id = cwd.share_key_id;
    tItem.sync_pid = cwd.sync_id;
    tItem.linkID = cwd.linkID;

    const encShareName = await this.linkFileListService.encName(tItem.filename).catch((err) => {
      throw err;
    });
    tItem.enc_share_name = encShareName;
    return tItem;
  }

  public async mkCompatUrl(tItem: ITransferItemDownload, action: number): Promise<string> {
    const disp = action == this.ACT_DOWNLOAD ? 'attachment' : 'inline';
    let mfsUrl: string,
      host = window.location.href;
    if (window.location.search) {
      host = host.replace(window.location.search, '');
    }

    const rsaDatakey = await this.syncCryptService.compatDatakeyEncrypt(
      this.syncCryptService.bytesToB64(tItem.data_key),
    );
    const encHost = await this.syncCryptService.compatDatakeyEncrypt(this.base64Service.encode(host));

    this.syncCookieService.deleteCookie('signature');
    this.syncCookieService.deleteCookie('passwordlock');
    tItem.disposition = disp;

    if (tItem.linkID) {
      let params = this.urlService.getDownloadPubLinkParams(tItem, rsaDatakey, encHost);
      if (action == this.ACT_STREAM_DOC) {
        params.preview = 'pdf';
        params.action = 'stream';
      }
      // Password-locked links send the passwordlock as a sibling of `req` so
      // sync-core mints a `pltoken` (bound to the link, short-lived) without it
      // entering the signed set. All links now route direct to serverpools.
      const signReq: any = { req: params };
      if (tItem.linkpasswordlock) {
        signReq.passwordlock = tItem.linkpasswordlock;
      }
      const result = await this.apiService.execute<any>('linksignrequest', signReq).catch((err) => {
        throw err;
      });
      params = result.response;
      if (tItem.linkpasswordlock) {
        this.syncCookieService.setDownloadPubLink(tItem.linkpasswordlock);
        if (!result.pltoken) {
          this.loggerService.error(
            'linksignrequest returned no pltoken for a password-locked link; the request will be rejected',
          );
        }
      }
      mfsUrl = await this.urlService.mkDownloadPubLink(tItem, params, result.pltoken);
      return mfsUrl;
    } else {
      const ts = Date.now();
      if (action == this.ACT_STREAM_DOC) {
        mfsUrl = await this.urlService.mkPreviewPubUser(tItem, rsaDatakey, ts);
      } else {
        mfsUrl = await this.urlService.mkDownloadPubUser(tItem, rsaDatakey, ts);
      }
      const input = new BaseApiInput();
      input.servtime = ts;
      const defaults = await this.syncCryptService.signApiReq(input).catch((err) => {
        throw err;
      });
      mfsUrl = mfsUrl + '&access_token=' + defaults.access_token;
      this.syncCookieService.setDownloadPubUser(defaults.signature);
      return mfsUrl;
    }
  }

  public getVideoMimeType(ext: string): string {
    switch (ext) {
      case 'mp4':
      case 'm4v':
      case 'm4p':
        return 'video/mp4';
      case 'webm':
        return 'video/webm';
      case 'mpg':
      case 'mpeg':
        return 'video/mpeg';
      case '3gp':
        return 'video/3gpp';
      case 'ogv':
      case 'ogg':
        return 'video/ogg';
      case 'mov':
        return 'video/quicktime';
      case 'wmv':
        return 'video/wmv';
      case 'asf':
        return 'video/x-ms-asf';
      case 'flv':
        return 'video/x-flv';
      case 'avi':
        return 'video/avi';
      case 'mkv':
        return 'video/x-matroska';
      default:
        return null;
    }
  }

  private getAudioMimeType(ext: string): string | null {
    switch (ext) {
      case 'mp3':
        return 'audio/mp3';
      case 'wav':
        return 'audio/wav';
      case 'aac':
        return 'audio/aac';
      default:
        return null;
    }
  }

  public async getStreamingDetails(item: FileItem): Promise<StreamItem> {
    const url = await this.getCompatUrl(item, this.ACT_PREVIEW);
    let mimeType = '';

    const ext = item.name ? item.name.split('.').pop()?.toLowerCase() : '';

    if (this.fileItemService.isVideo(item)) {
      mimeType = this.getVideoMimeType(ext);
    } else if (this.fileItemService.isAudio(item)) {
      mimeType = this.getAudioMimeType(ext);
    } else {
      mimeType = null;
    }

    return {
      url,
      mimeType,
    };
  }

  public resetView(): void {
    this.view = {
      download: {
        queue: [],
        queueIdx: 0,
        isProcessing: false,
        activeItemCount: 0,
      },
      upload: {
        queue: [],
        queueIdx: 0,
        isProcessing: false,
        activeItemCount: 0,
      },
      single: undefined,
      isUpload: false,
      isDownload: false,
      enabled: this.browserSupportService.testXhr2(),
      pending: 0,
      cannotViewSave: false,
      transferLabel: 'Upload',
      isMinimized: false,
    };
  }

  private navigationHandler = (): string => {
    if (this.view.download.isProcessing || this.view.upload.isProcessing) {
      return 'There is unfinished upload/download pending.';
    }
    return undefined;
  };
}
